// @mui
import Card from '@mui/material/Card';
import { Box, Paper, TextField, Grid, Stack, Button, Typography, CircularProgress } from '@mui/material';
import CardContent from '@mui/material/CardContent';
// hooks
import { useEffect, useState, useRef } from 'react';
// components
import Iconify from 'src/components/iconify';
import { getRequest, postRequestForWebsite } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from 'src/services/fetching';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import useSolutionSection from 'src/hooks/use-solution-page';
// import useSolutionsSection from 'src/hooks/use-solutions-data';

// ----------------------------------------------------------------------

export default function SolutionSector() {
  const { enqueueSnackbar } = useSnackbar();
  const { fetchOnMount } = useSolutionSection();
  const solutionPageData = useSelector((state) => state.website.solutionPage);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    // Page Header
    pageTitle: "",
    pageTitleColor: "#000000", // Added color field for page title
    pageSubtitle: "",
    pageSubtitleColor: "#555555", // Added color field for page subtitle

    // Solutions Sections
    sections: [
      {
        // id: '',
        title: "",
        subtitle: "",
        description: "",
        buttonText: "",
        buttonLink: "#",
        buttonColor: "#4CAF50",
        imageUrl: "",
        imagePosition: "" // left or right
      },
    ],

    borderRadius: 1
  });

  useEffect(() => {
    fetchOnMount();
  }, [fetchOnMount]);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      ...solutionPageData?.solution_page
    }));
  }, [solutionPageData]);

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        current[key] = { ...current[key] };
        current = current[key];
      }
      current[keys[keys.length - 1]] = value;

      return newData;
    });
  };

  const updateSection = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) =>
        i === index ? { ...section, [field]: value } : section
      )
    }));
  };

  const addSection = () => {
    const newSection = {
      id: `section-${Date.now()}`,
      title: "",
      subtitle: "",
      description: "",
      buttonText: "View Details",
      buttonLink: "#",
      buttonColor: "#4CAF50",
      imageUrl: "",
      imagePosition: "left"
    };

    setFormData(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
  };

  const removeSection = (index) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const moveSection = (index, direction) => {
    const newSections = [...formData.sections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex >= 0 && targetIndex < newSections.length) {
      [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
      setFormData(prev => ({ ...prev, sections: newSections }));
    }
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      const response = await postRequestForWebsite(
        endpoints.admin_dashboard.website.solution_page.solution_section,
        {
          attributes: {
            ...JSON.parse(JSON.stringify(solutionPageData)),
            solution_page: formData
          }
        },
        true
      );

      if (response.metadata?.success) {
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h4" paragraph>
          Solutions Section Management
        </Typography>

        {/* Page Header Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Page Header</Typography>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Page Title</Typography>
            <TextField
              fullWidth
              value={formData.pageTitle}
              onChange={(e) => updateFormData('pageTitle', e.target.value)}
              placeholder="Enter page title"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Page Title Color</Typography>
            <TextField
              fullWidth
              type="color"
              value={formData.pageTitleColor}
              onChange={(e) => updateFormData('pageTitleColor', e.target.value)}
              sx={{ '& input': { height: 40 } }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Page Subtitle</Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={formData.pageSubtitle}
              onChange={(e) => updateFormData('pageSubtitle', e.target.value)}
              placeholder="Enter page subtitle/description"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Page Subtitle Color</Typography>
            <TextField
              fullWidth
              type="color"
              value={formData.pageSubtitleColor}
              onChange={(e) => updateFormData('pageSubtitleColor', e.target.value)}
              sx={{ '& input': { height: 40 } }}
            />
          </Box>
        </Box>

        {/* Solutions Sections */}
        <Box sx={{ mb: 5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center">
              <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
              <Typography variant="h6">Solutions Sections</Typography>
            </Stack>
            <Button
              variant="contained"
              color="success"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={addSection}
            >
              Add New Section
            </Button>
          </Stack>

          {formData.sections.map((section, index) => (
            <Paper
              key={section.id}
              variant="outlined"
              sx={{ p: 4, mb: 4, bgcolor: 'background.neutral' }}
            >
              {/* Section Header */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h6" color="primary">
                  Section {index + 1}: {section.title || 'Untitled'}
                </Typography>
                {/* <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={index === 0}
                    onClick={() => moveSection(index, 'up')}
                    startIcon={<Iconify icon="eva:arrow-up-fill" />}
                  >
                    Up
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={index === formData.sections.length - 1}
                    onClick={() => moveSection(index, 'down')}
                    startIcon={<Iconify icon="eva:arrow-down-fill" />}
                  >
                    Down
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="contained"
                    onClick={() => removeSection(index)}
                    startIcon={<Iconify icon="mingcute:close-line" />}
                  >
                    Remove
                  </Button>
                </Stack> */}
              </Stack>

              <Grid container spacing={3}>
                {/* Content Fields */}
                <Grid item xs={12} md={8}>
                  <Stack spacing={3}>
                    {/* <TextField
                      fullWidth
                      label="Section ID"
                      value={section.id}
                      onChange={(e) => updateSection(index, 'id', e.target.value)}
                      helperText="Unique identifier for this section"
                    /> */}

                    <TextField
                      fullWidth
                      label="Title"
                      value={section.title}
                      onChange={(e) => updateSection(index, 'title', e.target.value)}
                      placeholder="e.g., By Industrial Challenges"
                    />

                    <TextField
                      fullWidth
                      label="Subtitle"
                      value={section.subtitle}
                      onChange={(e) => updateSection(index, 'subtitle', e.target.value)}
                      placeholder="e.g., Overcoming Construction Hurdles"
                    />

                    <TextField
                      fullWidth
                      label="Description"
                      multiline
                      rows={4}
                      value={section.description}
                      onChange={(e) => updateSection(index, 'description', e.target.value)}
                      placeholder="Enter section description"
                    />

                    {/* Button Configuration */}
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 2 }}>Button Configuration</Typography>
                      <Stack spacing={2}>
                        <TextField
                          fullWidth
                          label="Button Text"
                          value={section.buttonText}
                          onChange={(e) => updateSection(index, 'buttonText', e.target.value)}
                        />
                        <TextField
                          fullWidth
                          label="Button Link"
                          value={section.buttonLink}
                          onChange={(e) => updateSection(index, 'buttonLink', e.target.value)}
                          placeholder="Enter URL or path"
                        />
                        <TextField
                          fullWidth
                          label="Button Color"
                          type="color"
                          value={section.buttonColor}
                          onChange={(e) => updateSection(index, 'buttonColor', e.target.value)}
                          sx={{ '& input': { height: 40 } }}
                        />
                      </Stack>
                    </Paper>
                  </Stack>
                </Grid>

                {/* Image Configuration */}
                <Grid item xs={12} md={4}>
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="Image URL"
                      value={section.imageUrl}
                      onChange={(e) => updateSection(index, 'imageUrl', e.target.value)}
                      placeholder="Enter image URL"
                      helperText="Provide a valid image URL"
                    />

                    <TextField
                      fullWidth
                      select
                      label="Image Position"
                      value={section.imagePosition}
                      onChange={(e) => updateSection(index, 'imagePosition', e.target.value)}
                      SelectProps={{ native: true }}
                    >
                      <option value="left">Left</option>
                      <option value="right">Right</option>
                    </TextField>

                    {/* Image Preview */}
                    {section.imageUrl && (
                      <Box
                        sx={{
                          width: '100%',
                          height: 200,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          position: 'relative'
                        }}
                      >
                        <Box
                          component="img"
                          src={section.imageUrl}
                          alt={`${section.title} Preview`}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            display: 'none',
                            color: 'text.disabled',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%'
                          }}
                        >
                          Invalid image URL
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Box>

        {/* Action Buttons */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{ pt: 3, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <LoadingButton
            loading={loading}
            variant="contained"
            color="primary"
            onClick={handleSave}
            startIcon={<Iconify icon="eva:save-fill" />}
            size="large"
          >
            Update
          </LoadingButton>
        </Stack>
      </CardContent>
    </Card>
  );
}
